import React, { FC, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import MuiTable from '@mui/material/Table';
import MuiTableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import {
    TablePropsType,
    TableColumnType,
    TableRefType,
    GetAllPrintRowsType,
    ExportTypeType,
    GridRequestType,
    OnChangeOptionsType,
    TablePropsOptionsType,
    ExtendOptionsType,
} from 'src/types/table';
import MuiBox, { BoxProps } from '@mui/material/Box';
import Pagination from './Pagination';
import TableHeader from './TableHeader';
import TableBody from './TableBody';
import { styled } from '@mui/material/styles';
import { colors, sizes } from 'src/themes/default';
import { exportData, exportDataGrid, getMaxHeight, isObjectOfBooleans, sortColumns } from './functions';
import ShowColumns from './ShowColumns';
import useWindowSize from 'src/utils/useWindowSize';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { COLUMNS_ORDER_SURFIX } from './constants';

const Box = styled(MuiBox)<BoxProps>(({ theme }) => ({
    '& .MuiPaper-root': {
        '& .MuiTableHead-root': {
            backgroundColor: colors.background_B3,
            background: `linear-gradient(180deg, ${colors.background_B3} 38px, ${colors.background_B0} 38px)`,

            '& .MuiTableCell-head, & .MuiTableCell-head.MuiTableCell-stickyHeader': {
                backgroundColor: colors.background_B0,
                backgroundImage: `linear-gradient(180deg, ${colors.background_B3} 38px, ${colors.background_B0} 38px)`,
                backgroundSize: '100% 100%',
                backgroundPosition: '0 0',
                backgroundRepeat: 'no-repeat',
                // background: `transparent`,
                fontSize: sizes.small_FS,
                lineHeight: sizes.small_LH,
                fontWeight: sizes.bold_W,
                padding: 0,

                // '& > span': {
                '& > .MuiTableSortLabel-root': {
                    display: 'block',
                    width: '100%',
                    // maxWidth: '200px',
                    padding: '8px 10px 8px 10px',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    minHeight: '38px',

                    '& > .MuiTableSortLabel-icon': {
                        marginBottom: '-3px',
                    },
                },

                '& .MuiFormControl-root': {
                    padding: '8px 5px',
                    // backgroundColor: colors.background_B0,
                },

                '&.row-action': {
                    '& svg': {
                        position: 'absolute',
                        // right: '10px',
                        // color: colors.text_T3,
                        color: colors.icon,
                        opacity: 1,
                    },

                    '&:hover svg': {
                        color: colors.text_T2,
                    },

                    '& .Mui-active svg': {
                        // color: colors.text_T1,
                        color: colors.icon,
                    },
                },

                '&.row-select ': {
                    padding: '0px',
                    width: sizes.table_select_col_W,

                    '& .MuiCheckbox-root': {
                        paddingBottom: '8px',
                    },

                    '& .MuiFormControl-root': {
                        height: '47px',
                    },
                },

                '& .has-value .MuiInputBase-root.MuiOutlinedInput-root fieldset': {
                    borderColor: colors.primary_O,
                },
            },
        },

        '& .MuiTableBody-root': {
            '& .MuiTableRow-root': {
                '&:hover': {
                    // backgroundColor: colors.background_B1,
                },

                '& .MuiTableCell-body': {
                    // ak bude 100% tak prvy stlpec moze byt viac siroky ako potrebujeme
                    // width: '100%',
                    // maxWidth: '200px',
                    padding: '13px 11px 13px 10px',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                },

                '& .row-select ': {
                    padding: '0px',
                    width: sizes.table_select_col_W,

                    '& .MuiCheckbox-root': {
                        // padding: '0px',
                    },
                },
            },
        },

        '& .row-select ': {
            '& .MuiCheckbox-root': {
                padding: '10px 10px 10px 10px',
            },

            '& svg': {
                width: '20px',
                height: '20px',
            },
        },
    },

    '&.table-row-actions': {
        '& .MuiPaper-root': {
            // Column collapses to 0; overlays sit on top and only cover as wide as their icons
            '& .MuiTableHead-root .MuiTableCell-head.row-action, & .MuiTableHead-root .MuiTableCell-head.row-action.MuiTableCell-stickyHeader': {
                position: 'sticky',
                right: '0px',
                top: 0,
                width: sizes.table_action_col_W,
                minWidth: sizes.table_action_col_W,
                maxWidth: sizes.table_action_col_W,
                padding: '0px',
                overflow: 'visible',
                zIndex: 10,
                backgroundColor: colors.background_B0,
                backgroundImage: `linear-gradient(180deg, ${colors.background_B3} 38px, ${colors.background_B0} 38px)`,
                backgroundSize: '100% 100%',
                backgroundPosition: '0 0',
                backgroundRepeat: 'no-repeat',

                '& .MuiButton-root': {
                    height: '38px',
                    minWidth: sizes.table_action_col_W,
                    padding: 'unset',
                    position: 'absolute',
                    top: '0px',
                    right: '0px',
                    width: sizes.table_action_col_W,
                    background: colors.background_B3,

                    '&:hover': {
                        '& svg': {
                            color: colors.icon,
                        },
                    },
                    '& svg': {
                        width: '20px',
                        height: '20px',
                        color: colors.icon,
                    },

                    '&.table-clear-filters': {
                        position: 'absolute',
                        top: '38px',
                        bottom: '1px',
                        height: 'auto',
                        background: colors.background_B0,

                        '& svg': {
                            width: '30px',
                            height: '30px',
                            fill: colors.primary_O,
                        },
                    },
                },
            },

            '& .MuiTableBody-root': {
                backgroundColor: colors.background_W,
            },

            '& .MuiTableBody-root .MuiTableRow-root': {
                backgroundColor: colors.background_W,

                '&:hover': {
                    backgroundColor: colors.background_B1,

                    '& .MuiTableCell-body': {
                        backgroundColor: colors.background_B1,
                    },

                    '& .MuiTableCell-body.row-action .row-actions-inline': {
                        background: colors.background_B1,
                    },
                },

                '&.table-row-selected': {
                    backgroundColor: colors.primary_1,

                    '& .MuiTableCell-body': {
                        backgroundColor: colors.primary_1,
                    },

                    '& .MuiTableCell-body.row-action .row-actions-inline': {
                        background: colors.primary_1,
                    },

                    '&:hover': {
                        backgroundColor: colors.background_B1,

                        '& .MuiTableCell-body': {
                            backgroundColor: colors.background_B1,
                        },

                        '& .MuiTableCell-body.row-action .row-actions-inline': {
                            background: colors.background_B1,
                        },
                    },
                    '&.table-row-open-popup': {
                        backgroundColor: colors.background_B1,

                        '& .MuiTableCell-body': {
                            backgroundColor: colors.background_B1,
                        },

                        '& .MuiTableCell-body.row-action .row-actions-inline': {
                            background: colors.background_B1,
                        },
                    },
                },
                '&.table-row-open-popup': {
                    backgroundColor: colors.background_B1,

                    '& .MuiTableCell-body': {
                        backgroundColor: colors.background_B1,
                    },

                    '& .MuiTableCell-body.row-action .row-actions-inline': {
                        background: colors.background_B1,
                    },
                },

                '& .MuiTableCell-body': {
                    backgroundColor: colors.background_W,

                    '&.row-action': {
                        position: 'sticky',
                        right: '0px',
                        top: 'auto',
                        bottom: 'auto',
                        width: sizes.table_action_col_W,
                        minWidth: sizes.table_action_col_W,
                        maxWidth: sizes.table_action_col_W,
                        padding: '0px',
                        overflow: 'visible',
                        zIndex: 1,
                        background: colors.background_W,
                        borderBottom: `1px solid ${theme.palette.divider}`,

                        '& .row-actions-inline': {
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: '1px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            background: colors.background_W,
                            overflow: 'hidden',
                        },

                        '& .MuiButton-root': {
                            height: sizes.table_action_col_W,
                            maxHeight: '100%',
                            width: sizes.table_action_col_W,
                            minWidth: sizes.table_action_col_W,
                            padding: 'unset',
                            background: 'transparent',

                            '& svg': {
                                width: '25px',
                                height: '25px',
                            },
                        },
                    },
                },
            },
        },
    },

    [theme.breakpoints.down('sm')]: {
        '& .MuiTablePagination-root': {
            '& .MuiTablePagination-selectLabel': {
                display: 'none',
            },
        },
    },
}));

const Table = forwardRef<TableRefType, TablePropsType>((props, ref) => {
    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down('sm'));
    const size = useWindowSize();

    const [visilbeColumns, setVisilbeColumns] = useState<TableColumnType[]>([]);
    const [showEditColumns, setShowEditColumns] = useState<boolean>(false);

    // handle all change options, to avoidd duplicite calls
    let onChangeOptions: undefined | OnChangeOptionsType = undefined;
    if (props.onChangeOptions) {
        onChangeOptions = (newOptions: TablePropsOptionsType) => {
            if (JSON.stringify(props.options) !== JSON.stringify(newOptions)) {
                props.onChangeOptions?.(newOptions);
            }
        };
    }

    useImperativeHandle(ref, () => ({
        exportTable(
            type: ExportTypeType,
            filename: string,
            getPrintRows?: GetAllPrintRowsType,
            extendedColmns?: TableColumnType[]
        ) {
            exportData(
                type,
                filename,
                props.allFilteredRows || props.rows,
                visilbeColumns,
                props.selectRowIdKey || props.rowIdKey,
                getPrintRows,
                props.selectedRows,
                props.columsLabelPrefix,
                props.customRender,
                props.selectOptions,
                extendedColmns
            );
        },
        exportTableGrid(
            type: ExportTypeType,
            filename: string,
            getGridParams: () => GridRequestType,
            requestUrl: string,
            extendedColmns?: TableColumnType[],
            extendOptions?: ExtendOptionsType
        ) {
            exportDataGrid(
                type,
                filename,
                getGridParams,
                requestUrl,
                visilbeColumns,
                props.columsLabelPrefix,
                props.selectedRows,
                props.selectOptions,
                extendedColmns,
                extendOptions
            );
        },
    }));

    const showToggleColumns = !!props.userSettingsName;

    useEffect(() => {
        if (onChangeOptions) {
            let newVisilbeColumns: TableColumnType[] = [];
            let newColumnsInOrder: TableColumnType[] = [];

            if (props.userSettingsName) {
                newColumnsInOrder = sortColumns(
                    props.allColumns,
                    props.userSettingsData?.[`${props.userSettingsName}${COLUMNS_ORDER_SURFIX}`]
                );

                const settingsColumns = props.userSettingsData && props.userSettingsData[props.userSettingsName];
                if (settingsColumns && isObjectOfBooleans(settingsColumns)) {
                    newVisilbeColumns = newColumnsInOrder.filter(
                        (column) => column.allwaysShow || settingsColumns[column.key]
                    );
                } else {
                    newVisilbeColumns = newColumnsInOrder.filter((column) => column.allwaysShow || column.defaultShow);
                }
            } else {
                newColumnsInOrder = [...props.allColumns];
                newVisilbeColumns = newColumnsInOrder;
            }

            setVisilbeColumns(newVisilbeColumns);
            onChangeOptions({ ...props.options, columns: newVisilbeColumns });
        }
    }, [props.allColumns, props.userSettingsData]);

    useEffect(() => {
        if (props.setSelectedRows && props.selectedRows?.length) {
            props.setSelectedRows([]);
        }
    }, [props.rows, props.options]);
    // maybe better on changed rows
    // }, [props.rows]);

    useEffect(() => {
        // if page is more then data count
        if (
            onChangeOptions &&
            props.options.page > 1 &&
            (props.options.page - 1) * props.options.items > (props.totalCount || props.options.totalCount || 0)
        ) {
            onChangeOptions({ ...props.options, page: 1 });
        }
    }, [props.totalCount, props.options.totalCount]);

    useEffect(() => {
        if (props.options && props.optionsSessionName) {
            sessionStorage.setItem(props.optionsSessionName, JSON.stringify(props.options));
        }
    }, [props.options]);

    return (
        <Box
            sx={{ width: '100%', ...(props.sx || {}) }}
            className={!!props.rowActions || props.showClearFilters ? 'table-row-actions' : ''}
            data-testid={props.dataTestId}
        >
            {/* <MuiTableContainer component={Paper} sx={{ maxHeight: getMaxHeight(props, size, mobile), width: '100%' }}> */}
            <MuiTableContainer
                component={Paper}
                sx={{
                    height: props.fullHeight ? getMaxHeight(props, size, mobile) : undefined,
                    maxHeight: !props.fullHeight ? getMaxHeight(props, size, mobile) : undefined,
                    width: '100%',
                    overflow: 'auto',
                }}
            >
                <MuiTable stickyHeader size="small">
                    <TableHeader
                        visilbeColumns={visilbeColumns}
                        options={props.options}
                        onChangeOptions={onChangeOptions}
                        selectOptions={props.selectOptions}
                        columsLabelPrefix={props.columsLabelPrefix}
                        disableFilter={props.disableFilter}
                        rowIdKey={props.rowIdKey}
                        selectRowIdKey={props.selectRowIdKey}
                        rows={props.rows}
                        selectedRows={props.selectedRows}
                        setSelectedRows={props.setSelectedRows}
                        showToggleColumns={showToggleColumns}
                        openToggleColumns={() => setShowEditColumns(true)}
                        rowActions={props.rowActions}
                        showClearFilters={props.showClearFilters}
                        optionStyleFce={props.optionStyleFce}
                    />
                    <TableBody
                        rowIdKey={props.rowIdKey}
                        selectRowIdKey={props.selectRowIdKey}
                        customRow={props.customRow}
                        customRowActions={props.customRowActions}
                        loading={props.loading}
                        visilbeColumns={visilbeColumns}
                        rows={props.rows}
                        allFilteredRows={props.allFilteredRows}
                        selectOptions={props.selectOptions}
                        rowActions={props.rowActions}
                        selectedRows={props.selectedRows}
                        setSelectedRows={props.setSelectedRows}
                        customRender={props.customRender}
                        rowSxFce={props.rowSxFce}
                        disableFilter={props.disableFilter}
                        sumCols={props.sumCols}
                        sumColsFilter={props.sumColsFilter}
                        showInModal={props.showInModal}
                        customRowRender={props.customRowRender}
                        showClearFilters={props.showClearFilters}
                    />
                </MuiTable>
            </MuiTableContainer>
            <MuiBox sx={{ display: 'flex', alignItems: 'center' }}>
                {props.footerLeft && <MuiBox sx={{ display: 'flex', alignItems: 'center' }}>{props.footerLeft}</MuiBox>}
                <MuiBox sx={{ flex: 1 }}>
                    <Pagination onChangeOptions={onChangeOptions} options={props.options} totalCount={props.totalCount} />
                </MuiBox>
            </MuiBox>
            {showEditColumns && props.userSettingsName && props.userSettingsData && props.editUserSettings && (
                <ShowColumns
                    visilbeColumns={visilbeColumns}
                    onClose={() => setShowEditColumns(false)}
                    allColumns={props.allColumns}
                    columsLabelPrefix={props.columsLabelPrefix}
                    userSettingsName={props.userSettingsName}
                    userSettingsData={props.userSettingsData}
                    editUserSettings={props.editUserSettings}
                />
            )}
        </Box>
    );
});

export default Table;
